import React from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { Button } from "../../../../components/ui/Button";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { classNames } from "primereact/utils";
import { useForm, Controller } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toggleDialog } from "../../../../redux/Slices/Global/globalSlice";
import { setSelectedAgency, setIsEditingAgency } from "../../../../redux/Slices/adMarketplaceSlice";
import { useCreateAgency, useUpdateAgency } from "../../../../queries/adMarketplace";

const buildAgencyStatusOpts = (t) => [
    { label: t("status.active"),                  value: "active"    },
    { label: t("status.inactive"),                value: "inactive"  },
    { label: t("ad_marketplace.status_suspended"), value: "suspended" },
];

export default function AgencyDialog() {
    const { t }      = useTranslation();
    const dispatch   = useDispatch();
    const openDialog = useSelector((s) => s.global.openDialog);
    const { selectedAgency, isEditingAgency } = useSelector((s) => s.adMarketplace);

    const STATUS_OPTS = buildAgencyStatusOpts(t);

    const createMutation = useCreateAgency();
    const updateMutation = useUpdateAgency();

    const { control, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: isEditingAgency && selectedAgency ? {
            name:           selectedAgency.name           ?? "",
            email:          selectedAgency.email          ?? "",
            phone:          selectedAgency.phone          ?? "",
            website:        selectedAgency.website        ?? "",
            address:        selectedAgency.address        ?? "",
            contact_person: selectedAgency.contact_person ?? "",
            status:         selectedAgency.status         ?? "active",
        } : {
            name: "", email: "", phone: "", website: "", address: "", contact_person: "", status: "active",
        },
    });

    const onClose = () => {
        reset();
        dispatch(setIsEditingAgency(false));
        dispatch(setSelectedAgency(null));
        dispatch(toggleDialog("AgencyDialog"));
    };

    const onSubmit = (values) => {
        const mutation = isEditingAgency
            ? updateMutation.mutate({ id: selectedAgency.id, ...values }, { onSuccess: onClose })
            : createMutation.mutate(values, { onSuccess: onClose });
    };

    const isLoading = createMutation.isLoading || updateMutation.isLoading;

    return (
        <Dialog
            visible={!!openDialog?.AgencyDialog}
            onHide={onClose}
            header={isEditingAgency ? t("ad_marketplace.edit_agency") : t("ad_marketplace.new_agency")}
            style={{ width: "480px" }}
            modal
        >
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 pt-2">
                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_agency_name")}</label>
                        <Controller name="name" control={control} rules={{ required: true }} render={({ field }) => (
                            <InputText {...field} className={classNames("w-full text-sm", { "p-invalid": errors.name })} />
                        )} />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("form.email")}</label>
                        <Controller name="email" control={control} rules={{ required: true }} render={({ field }) => (
                            <InputText {...field} type="email" className={classNames("w-full text-sm", { "p-invalid": errors.email })} />
                        )} />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("form.phone")}</label>
                        <Controller name="phone" control={control} render={({ field }) => (
                            <InputText {...field} className="w-full text-sm" placeholder="+1 555 000 0000" />
                        )} />
                    </div>
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_website")}</label>
                        <Controller name="website" control={control} render={({ field }) => (
                            <InputText {...field} className="w-full text-sm" placeholder="https://" />
                        )} />
                    </div>
                </div>

                <div>
                    <label className="block text-xs text-gray-500 mb-1">{t("ad_marketplace.field_contact_person")}</label>
                    <Controller name="contact_person" control={control} render={({ field }) => (
                        <InputText {...field} className="w-full text-sm" placeholder={t("ad_marketplace.contact_person_ph")} />
                    )} />
                </div>

                <div>
                    <label className="block text-xs text-gray-500 mb-1">{t("form.address")}</label>
                    <Controller name="address" control={control} render={({ field }) => (
                        <InputText {...field} className="w-full text-sm" />
                    )} />
                </div>

                {isEditingAgency && (
                    <div>
                        <label className="block text-xs text-gray-500 mb-1">{t("table.status")}</label>
                        <Controller name="status" control={control} render={({ field }) => (
                            <Dropdown {...field} options={STATUS_OPTS} className="w-full text-sm" />
                        )} />
                    </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" label={t("buttons.cancel")} className="p-button-text p-button-sm" onClick={onClose} />
                    <Button type="submit" label={isEditingAgency ? t("buttons.save") : t("ad_marketplace.create_agency")}
                        icon="pi pi-check" className="p-button-primary p-button-sm" loading={isLoading} />
                </div>
            </form>
        </Dialog>
    );
}
